import { Eye, EyeOff } from 'lucide-react'
import { useState, type FormEvent } from 'react'
import { useNavigate } from 'react-router-dom'
import { TelegramLoginView, type TelegramLoginPhase } from '@/components/TelegramLogin'
import { BrandLockup } from '@/components/ui/BrandLockup'
import { Button } from '@/components/ui/Button'
import { Card } from '@/components/ui/Card'
import { FormField, Input } from '@/components/ui/Input'
import { ApiError } from '@/lib/api'
import { useAuth } from '@/lib/auth'
import { documentTitle, useDocumentTitle } from '@/lib/documentTitle'
import { isTelegramLaunch, miniappErrorFatal, miniappErrorText, telegramInitData } from '@/lib/telegram'
import { typo } from '@/lib/typo'

/** Sign-in (WP-16): every field has a visible label tied to it, the 2FA field says what it
 *  wants, the password can be shown to check it, and on a phone the form sits on the page
 *  without a card around it. */
export function Login() {
  useDocumentTitle(documentTitle('Вход'))
  const [byEmail, setByEmail] = useState(() => !isTelegramLaunch())
  if (!byEmail) return <TelegramLogin onUseEmail={() => setByEmail(true)} />
  return <EmailLogin />
}

/** The admin opened from Telegram (lib/telegram): initData → the 2FA code → the session. */
function TelegramLogin({ onUseEmail }: { onUseEmail: () => void }) {
  const { startTelegram, verifyTelegram } = useAuth()
  const navigate = useNavigate()
  const [phase, setPhase] = useState<TelegramLoginPhase>('idle')
  const [challenge, setChallenge] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [fatal, setFatal] = useState(false)

  function fail(err: unknown, back: TelegramLoginPhase) {
    if (err instanceof ApiError) {
      const end = miniappErrorFatal(err.status, err.detail)
      setError(miniappErrorText(err.status, err.detail))
      setFatal(end)
      setPhase(end ? 'idle' : back)
    } else {
      setError('Нет связи с сервером. Повторите через минуту.')
      setPhase(back)
    }
  }

  async function start() {
    const initData = telegramInitData()
    if (!initData) {
      setError('Telegram не передал данные входа. Откройте админку кнопкой в боте.')
      setFatal(true)
      return
    }
    setPhase('sending')
    setError(null)
    try {
      const next = await startTelegram(initData)
      if (next === '') return navigate('/') // an older server signed in without the code
      setChallenge(next)
      setPhase('code')
    } catch (err) {
      fail(err, 'idle')
    }
  }

  async function verify(code: string) {
    setPhase('verifying')
    setError(null)
    try {
      await verifyTelegram(challenge, code)
      navigate('/')
    } catch (err) {
      fail(err, 'code')
    }
  }

  return (
    <main className="grid min-h-dvh place-items-center bg-void px-4 py-10">
      <div className="flex w-full max-w-sm flex-col items-center gap-6">
        <BrandLockup size={32} />
        <Card className="w-full p-6 max-sm:border-0 max-sm:bg-transparent max-sm:p-0">
          <TelegramLoginView
            phase={phase}
            error={error}
            fatal={fatal}
            onStart={() => void start()}
            onVerify={(c) => void verify(c)}
            onUseEmail={onUseEmail}
          />
        </Card>
      </div>
    </main>
  )
}

function EmailLogin() {
  const { login } = useAuth()
  const navigate = useNavigate()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [totp, setTotp] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  async function submit(e: FormEvent) {
    e.preventDefault()
    setBusy(true)
    setError(null)
    try {
      await login(email, password, totp)
      navigate('/')
    } catch (err) {
      // 401 is deliberately vague server-side (no hint which factor failed); 423/429 carry
      // the lockout wait from Retry-After (see lib/api.ts).
      if (err instanceof ApiError && err.status === 401) {
        setError('Неверный email, пароль или код 2FA. Код можно использовать только один раз.')
      } else {
        setError(err instanceof ApiError ? err.message : 'Не удалось войти')
      }
    } finally {
      setBusy(false)
    }
  }

  return (
    <main className="grid min-h-dvh place-items-center bg-void px-4 py-10">
      <div className="flex w-full max-w-sm flex-col items-center gap-6">
        {/* The brand lockup is one of the few places the display role (Onest 700) is allowed (design §3.1, WP-06). */}
        <BrandLockup size={32} />
        {/* Below 640 px the form sits on the page: a card inside a 320 px screen is only a frame. */}
        <Card className="w-full p-6 max-sm:border-0 max-sm:bg-transparent max-sm:p-0">
          <h1 className="text-title-1 font-semibold text-ink-1">{typo('Вход в админку')}</h1>
          <form onSubmit={submit} className="mt-6 flex flex-col gap-4">
            <FormField label="Email">
              {(f) => (
                <Input
                  {...f}
                  type="email"
                  autoComplete="username"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              )}
            </FormField>
            <FormField label="Пароль">
              {(f) => (
                <div className="relative">
                  <Input
                    {...f}
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="current-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pr-12"
                    required
                  />
                  <button
                    type="button"
                    aria-controls={f.id}
                    aria-pressed={showPassword}
                    aria-label={showPassword ? 'Скрыть пароль' : 'Показать пароль'}
                    title={showPassword ? 'Скрыть пароль' : 'Показать пароль'}
                    onClick={() => setShowPassword((v) => !v)}
                    className="absolute inset-y-0 right-0 flex w-11 items-center justify-center rounded-sm text-ink-2 hover:text-ink-1"
                  >
                    {showPassword ? (
                      <EyeOff aria-hidden size={20} strokeWidth={1.75} />
                    ) : (
                      <Eye aria-hidden size={20} strokeWidth={1.75} />
                    )}
                  </button>
                </div>
              )}
            </FormField>
            <FormField label="Код 2FA" hint="Шесть цифр из приложения-аутентификатора">
              {(f) => (
                <Input
                  {...f}
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                  pattern="[0-9]{6}"
                  value={totp}
                  onChange={(e) => setTotp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  className="tnum"
                  required
                />
              )}
            </FormField>
            {error !== null && (
              <p className="text-caption text-danger" role="alert">
                ✕ {error}
              </p>
            )}
            <Button type="submit" variant="primary" size="lg" full loading={busy} loadingLabel="Вхожу…">
              Войти
            </Button>
          </form>
        </Card>
      </div>
    </main>
  )
}
